import React from "react";
import { ShieldCheck } from "lucide-react";

/**
 * "Dox verified this." — the soft citation footer that appears under any
 * Dox answer we can ground in the Knowledge Engine.
 *
 * Not a "Sources" heading. Not a citation footnote. Just Dox telling the
 * customer where his answer came from, in one calm line with a small
 * jurisdiction pill and confidence chip.
 *
 * Props:
 *   verification: { authority, jurisdiction, state, confidence, source_url } | null
 *   compact:      shrinks type + spacing for the floating widget bubble
 */
export default function DoxVerifiedLine({ verification, compact = false }) {
  if (!verification || !verification.authority) return null;
  const { authority, jurisdiction, confidence, source_url } = verification;
  const verified = confidence === "verified";

  const tone = verified
    ? "text-emerald-300/80"
    : "text-white/45";
  const chipTone = verified
    ? "border-emerald-400/25 bg-emerald-500/[0.08] text-emerald-300"
    : "border-white/12 bg-white/[0.04] text-white/55";

  const wrap = compact
    ? "mt-2 flex flex-wrap items-center gap-x-2 gap-y-1 text-[10.5px]"
    : "mt-3 flex flex-wrap items-center gap-x-2.5 gap-y-1.5 text-[11.5px]";

  return (
    <div
      data-testid="dox-verified-line"
      className={`${wrap} ${tone} tracking-[0.01em]`}
    >
      <span className="inline-flex items-center gap-1.5 opacity-90">
        <ShieldCheck size={compact ? 11 : 12} strokeWidth={2.4} />
        <span className="font-medium">Dox verified this</span>
      </span>
      <span className="opacity-40">·</span>
      {source_url ? (
        <a
          href={source_url}
          target="_blank"
          rel="noopener noreferrer"
          className="hover:text-white transition-colors underline decoration-current/25 underline-offset-4"
          data-testid="dox-verified-authority"
        >
          {authority}
        </a>
      ) : (
        <span data-testid="dox-verified-authority">{authority}</span>
      )}
      {jurisdiction && (
        <span
          data-testid="dox-verified-jurisdiction"
          className={`inline-flex items-center rounded-full border px-2 py-[1.5px] text-[10px] tracking-wide ${chipTone}`}
        >
          {jurisdiction}
        </span>
      )}
      <span
        data-testid="dox-verified-confidence"
        className={`inline-flex items-center rounded-full border px-2 py-[1.5px] text-[10px] tracking-wide ${chipTone}`}
      >
        {verified ? "Verified source" : "General guidance"}
      </span>
    </div>
  );
}
